import { Button } from "@progress/kendo-react-buttons";
import { ChevronDown, ChevronUp } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { ValidationErrorItem } from "./validationSummaryUtils";

export interface SlidePanelValidationErrorNavigationProps {
  activeErrorIndex: number;
  errorItems: ValidationErrorItem[];
  onNavigate: (fieldName: string, index: number) => void;
  i18nPrefix?: string;
  dataTestId?: string;
  previousErrorDataTestId?: string;
  nextErrorDataTestId?: string;
}

const SlidePanelValidationErrorNavigation: React.FC<
  SlidePanelValidationErrorNavigationProps
> = ({
  activeErrorIndex,
  errorItems,
  onNavigate,
  i18nPrefix = "slidePanel.validation",
  dataTestId = "slide-panel-validation-error-navigation",
  previousErrorDataTestId = "slide-panel-validation-previous-error",
  nextErrorDataTestId = "slide-panel-validation-next-error",
}) => {
  const { t } = useTranslation();
  if (!errorItems.length) return null;

  const activeIndex = Math.min(activeErrorIndex, errorItems.length - 1);
  const navigateByOffset = (offset: number) => {
    const nextIndex =
      (activeIndex + offset + errorItems.length) % errorItems.length;
    onNavigate(errorItems[nextIndex].name, nextIndex);
  };

  return (
    <div
      className="tlx-panel-validation-error-navigation"
      data-test-id={dataTestId}
    >
      <span>
        {t(`${i18nPrefix}.errorsRemaining`, {
          count: errorItems.length,
        })}
      </span>
      <Button
        type="button"
        fillMode="flat"
        size="small"
        data-test-id={previousErrorDataTestId}
        aria-label={t(`${i18nPrefix}.previousError`)}
        onClick={() => navigateByOffset(-1)}
      >
        <ChevronUp size={14} aria-hidden="true" />
      </Button>
      <Button
        type="button"
        fillMode="flat"
        size="small"
        data-test-id={nextErrorDataTestId}
        aria-label={t(`${i18nPrefix}.nextError`)}
        onClick={() => navigateByOffset(1)}
      >
        <ChevronDown size={14} aria-hidden="true" />
      </Button>
    </div>
  );
};

export default SlidePanelValidationErrorNavigation;
